import React from 'react';
import { Options } from '@/components/ui/combobox';
import { useFilters } from '@/hooks';
import { GET_CLIENTS_LAWYER_DIRECTORY, lawyerDirectoryService } from '@/api';
import { CaseClientsResponse } from '@/interfaces/lawyerDirectory';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Avatar, HeaderFilterTable } from '@/components/common';

interface Props {
	caseId: string;
	disabled?: boolean;
}

export const ClientsFilter: React.FC<Props> = ({ caseId, disabled }) => {
	const { setParamFilter, setParamSeveralFilters, hasFilter } = useFilters();
	const observer = React.useRef<IntersectionObserver | null>(null);
	const [data, setData] = React.useState<Options[]>([]);

	const fetchData = async ({
		pageParam = 1,
	}: {
		pageParam: number;
	}): Promise<CaseClientsResponse> => {
		const response = await lawyerDirectoryService.getClientsLists(
			pageParam,
			caseId
		);
		return response.data;
	};

	const {
		data: clientNameList,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		// isError,
		isLoading,
	} = useInfiniteQuery({
		initialData: undefined,
		initialPageParam: 1,
		queryKey: [GET_CLIENTS_LAWYER_DIRECTORY],
		queryFn: fetchData,
		getNextPageParam(lastPage) {
			return lastPage.page < lastPage.totalPages
				? lastPage.page + 1
				: undefined;
		},
	});

	const lastItemCallback = React.useCallback(
		(node: HTMLDivElement | null) => {
			if (isFetchingNextPage) return;
			if (observer.current) observer.current.disconnect();
			observer.current = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting && hasNextPage) {
					fetchNextPage();
				}
			});
			if (node) observer.current.observe(node);
		},
		[isFetchingNextPage, hasNextPage]
	);

	React.useEffect(() => {
		if (clientNameList) {
			const auxList: Options[] = [];
			const listFlatMapped =
				clientNameList?.pages.flatMap((page) => page.items) || [];
			listFlatMapped.map((item) =>
				auxList.push({
					label: item.first_name
						? `${item.first_name} ${item.last_name}`
						: 'N/N',
					value: item.user_id,
					icon: (
						<Avatar
							photoUrl={item.profile_picture || ''}
							size="small"
							firstname={`${item.first_name}`}
							lastname={`${item.last_name}`}
						/>
					),
				})
			);
			setData(auxList);
		}
	}, [clientNameList]);

	const handleSelectFilters = (value?: string) => {
		// specials filters
		switch (value) {
			case 'alphabetically':
				setParamFilter('isOrderAlph', hasFilter('isOrderAlph') ? '' : 'true');
				break;
			default:
				setParamSeveralFilters([
					{ label: 'isOrderAlph', value: '' }, // reset
					{ label: 'clientId', value: value },
				]);
				break;
		}
	};

	const handleClearAll = () => {
		setParamSeveralFilters([
			{ label: 'isOrderAlph', value: '' },
			{ label: 'clientId', value: '' },
		]);
	};

	return (
		<HeaderFilterTable
			disabled={disabled}
			title="Client Name"
			searchTitle="Client"
			filterOptions={data}
			isLoading={isLoading}
			classNamePopoverContent="lg:!w-[320px]"
			containerRef={lastItemCallback}
			handleClearAll={handleClearAll}
			handleSelectFilters={handleSelectFilters}
			hasFilter={hasFilter('isOrderAlph') || hasFilter('clientId')}
		/>
	);
};
